import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CreateResponse } from 'it/primatoo/openapi/api/common/schemas/CreateResponse';
import { AppAudit } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/AppAudit';
import { Summary } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/Summary';
import { ListUserColumnsResponse } from 'it/primatoo/openapi/api/org/schemas/common/ListUserColumnsResponse';
import { AppPenTest } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/AppPenTest';
import { UpdateAppPenTestRequest } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/UpdateAppPenTestRequest';
import {
  ListSeverityForSummaryResponse
} from 'it/primatoo/openapi/api/org/schemas/cybersecurity/ListSeverityForSummaryResponse';
import { UpdateAppAuditNoteRequest } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/UpdateAppAuditNoteRequest';
import { AppAuditCve } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/AppAuditCve';
import { UpdateAppAuditCveRequest } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/UpdateAppAuditCveRequest';
import { XDOMAIN, XTENANTID } from '@pt/pt-models';
import { ApiClientConfig } from './api-client-config';
import { SummaryAggregation } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/SummaryAggregation';
import { SummaryMetric } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/SummaryMetric';
import { UpdateAppAuditCveResponse } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/UpdateAppAuditCveResponse';

@Injectable({
  providedIn: 'root'
})
export class CyberSecurityOrgService {

  private readonly endpointOrg: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    public httpClient: HttpClient
  ) {
    this.endpointOrg = this.config.endPoint + '/api/org/v2/cyber-sec/';
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public getSummaryPetTestColumns(): Observable<ListUserColumnsResponse> {
    const url = this.endpointOrg + `summary/pen-test-columns/`;
    return this.httpClient.get<ListUserColumnsResponse>(url);
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public sendManualScan(): Observable<CreateResponse> {
    const url = this.endpointOrg + `scan`;
    let headers = new HttpHeaders();
    headers = headers.set(XTENANTID, 'primatoo');
    headers = headers.set(XDOMAIN, 'primatoo');
    return this.httpClient.post<CreateResponse>(url, {}, {headers});
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public getAppPetTest(appId: number): Observable<AppPenTest> {
    const url = this.endpointOrg + `apps/${appId}/pen-test/`;
    return this.httpClient.get<AppPenTest>(url);
  }


  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public updateAppPetTest(appId: number, request: UpdateAppPenTestRequest): Observable<void> {
    const url = this.endpointOrg + `apps/${appId}/pen-test/`;
    return this.httpClient.put<void>(url, request);
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public deletePenetrationTest(appId: number) {
    const url = this.endpointOrg + `apps/${appId}/pen-test/`;
    return this.httpClient.delete<void>(url);
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public getAppAuditColumns(): Observable<ListUserColumnsResponse> {
    const url = this.endpointOrg + `audit-columns/`;
    return this.httpClient.get<ListUserColumnsResponse>(url);
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public getPaColumns(): Observable<ListUserColumnsResponse> {
    const url = this.endpointOrg + `summary/pa-columns/`;
    return this.httpClient.get<ListUserColumnsResponse>(url);
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public getPenTestSeverityLevel(): Observable<ListSeverityForSummaryResponse> {
    const url = this.endpointOrg + `pen-test-severity-levels/`;
    return this.httpClient.get<ListSeverityForSummaryResponse>(url);
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public getAppAudit(appId: number): Observable<AppAudit> {
    const url = this.endpointOrg + `apps/${appId}/audit/`;
    return this.httpClient.get<AppAudit>(url);
  }


  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public patchAppAuditNote(appId: number, request: UpdateAppAuditNoteRequest): Observable<void> {
    const url = this.endpointOrg + `apps/${appId}/audit/note/`;
    return this.httpClient.patch<void>(url, request);
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public getPetTestColumns(): Observable<ListUserColumnsResponse> {
    const url = this.endpointOrg + `pen-test-columns/`;
    return this.httpClient.get<ListUserColumnsResponse>(url);
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public getAppAuditCve(appId: number): Observable<AppAuditCve> {
    const url = this.endpointOrg + `apps/${appId}/audit/cve/`;
    return this.httpClient.get<AppAuditCve>(url);
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public patchAppAuditCve(appId: number, request: UpdateAppAuditCveRequest): Observable<UpdateAppAuditCveResponse> {
    const url = this.endpointOrg + `apps/${appId}/audit/cve/`;
    return this.httpClient.patch<UpdateAppAuditCveResponse>(url, request);
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public getSummary(metric: SummaryMetric = SummaryMetric.SCORE,
                    aggregation: SummaryAggregation = SummaryAggregation.SUM): Observable<Summary> {
    const url = this.endpointOrg + `summary/?metric=${metric}&aggregation=${aggregation}`;
    return this.httpClient.get<Summary>(url);
  }

  /**
   *  Last update 01/12/2023 @Giovanni
   */
  public getCveColumns(): Observable<ListUserColumnsResponse> {
    const url = this.endpointOrg + `summary/cve-columns/`;
    return this.httpClient.get<ListUserColumnsResponse>(url);
  }

}
